/**
 * /email-templates — Plantillas Email (notificación documentos DIAN).
 *
 * Selector tax_client + tabla 3 doc_type (FE/NC/ND) con badge
 * custom/default + version + last update + acciones (Editar / Restaurar).
 *
 * Mismo patrón que /pdf-templates pero apuntando a email API.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Pencil, RotateCcw } from 'lucide-react'

import { ClientSelector } from '../pdf-templates/components/client-selector'
import { useTaxClients } from '@/hooks/api/use-tax-clients'
import {
  useDeleteEmailTemplate,
  useEmailTemplates,
} from '@/hooks/api/use-email-templates'
import {
  DOC_TYPES,
  DOC_TYPE_LABELS,
  type DocType,
  type EmailTemplate,
} from '@/lib/api-email-templates'

export function EmailTemplatesPage() {
  const [clientId, setClientId] = useState<string>('')

  const clients = useTaxClients()
  const templates = useEmailTemplates(clientId || undefined)
  const del = useDeleteEmailTemplate()

  const customByDocType: Partial<Record<DocType, EmailTemplate>> = {}
  for (const t of templates.data?.items ?? []) {
    customByDocType[t.doc_type] = t
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-wider text-fg-muted">
          ▸ Notificaciones
        </p>
        <h1 className="mt-1 text-3xl font-bold text-fg-primary">Plantillas Email</h1>
        <p className="mt-2 max-w-2xl text-sm text-fg-secondary">
          Email que se envía al adquirente cuando un documento es aceptado por
          DIAN (con XML+PDF adjuntos). Cada plantilla tiene 3 sub-templates:
          <strong> Subject</strong>, <strong>HTML</strong> y <strong>texto plano</strong>.
          Si un tax-client no tiene plantilla custom, se usa el default Taxor.
        </p>
      </div>

      <div className="mb-6 rounded-xl border border-border bg-bg-surface p-4 shadow-sm">
        <ClientSelector
          value={clientId}
          onChange={setClientId}
          clients={clients.data?.items ?? []}
          loading={clients.isLoading}
        />
      </div>

      {!clientId && (
        <div className="rounded-xl border border-border bg-bg-surface p-12 text-center shadow-sm">
          <Mail className="mx-auto h-10 w-10 text-fg-muted" />
          <p className="mt-4 text-sm text-fg-secondary">
            Seleccione un tax-client para gestionar sus plantillas email.
          </p>
        </div>
      )}

      {clientId && (
        <div className="overflow-hidden rounded-xl border border-border bg-bg-surface shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-bg-elevated">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Tipo de documento
                </th>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Origen
                </th>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Versión
                </th>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Última actualización
                </th>
                <th className="px-4 py-3 text-right font-semibold text-fg-secondary">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {DOC_TYPES.map((dt) => {
                const tpl = customByDocType[dt]
                const isCustom = !!tpl
                return (
                  <tr key={dt} className="border-t border-border">
                    <td className="px-4 py-3 font-medium text-fg-primary">
                      {DOC_TYPE_LABELS[dt]}
                    </td>
                    <td className="px-4 py-3">
                      {isCustom ? (
                        <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                          Custom
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-bg-elevated px-2 py-0.5 text-xs font-medium text-fg-muted">
                          Default Taxor
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-fg-secondary">
                      {isCustom ? `v${tpl.version}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-fg-secondary">
                      {isCustom && tpl.updated_at
                        ? new Date(tpl.updated_at).toLocaleString('es-CO')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/email-templates/${clientId}/${dt}`}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary"
                        >
                          <Pencil className="h-3 w-3" />
                          Editar
                        </Link>
                        {isCustom && (
                          <button
                            type="button"
                            disabled={del.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  `¿Eliminar la plantilla email custom de "${DOC_TYPE_LABELS[dt]}"? Volverá al default Taxor.`,
                                )
                              ) {
                                del.mutate({ clientId, docType: dt })
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Restaurar default
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
